import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

// Shared building blocks for the mobile app. Every page is assembled from
// these, which is what keeps eight very different tabs feeling like one phone
// app. Styling lives in Mobile.css and re-skins itself for the Zone theme, so
// nothing here needs to know which theme is active.

export const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

export function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

// findSkinImage is the shared O(1) lookup from skinIndex.js, re-exported
// here so the pages importing it from this module stay unchanged.
export { findSkinImage } from '../components/skinIndex';

export const hexToRgba = (hex, a) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(x => x + x).join('');
  if (h.length !== 6) return `rgba(129,140,248,${a})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/* ─── Number formatting ───────────────────────────────────────
   Full cents everywhere, matching the desktop tabs, so the same row never
   reads differently between phone and desktop. `compact` is only ever used
   for headline figures where the exact cents are shown right underneath. */

export const fmt = (n) => Math.abs(Number(n) || 0)
  .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const money  = (n) => `$${fmt(n)}`;
export const signed = (n) => `${(Number(n) || 0) >= 0 ? '+' : '−'}$${fmt(n)}`;
export const pct    = (n) => `${(Number(n) || 0) >= 0 ? '+' : '−'}${Math.abs(Number(n) || 0).toFixed(1)}%`;

/** 12345.67 → "$12.3k". Only for oversized hero numbers that would otherwise
 *  wrap on a 360px screen; always paired with the exact figure nearby. */
export function compactMoney(n) {
  const v = Math.abs(Number(n) || 0);
  const sign = (Number(n) || 0) < 0 ? '−' : '';
  if (v >= 1_000_000) return `${sign}$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 10_000)    return `${sign}$${(v / 1_000).toFixed(1)}k`;
  return `${sign}$${fmt(v)}`;
}

export const dateShort = (d) => {
  const t = d instanceof Date ? d : new Date(d);
  return Number.isNaN(t.getTime())
    ? 'n/a'
    : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const dateFull = (d) => {
  const t = d instanceof Date ? d : new Date(d);
  return Number.isNaN(t.getTime())
    ? 'n/a'
    : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ─── Page frame ─────────────────────────────────────────────
   Every page opens with the same three-part rhythm: a hero figure, a
   scrollable stat strip, then content. Consistency here is what makes the
   bottom-nav switch feel instant rather than jarring. */

export function Page({ children, className = '' }) {
  return <div className={`mb-page ${className}`.trim()}>{children}</div>;
}

export function Hero({ label, value, tone = 'neutral', sub, foot }) {
  return (
    <motion.div
      className={`mb-hero tone-${tone}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="mb-hero-label">{label}</div>
      <div className="mb-hero-value">{value}</div>
      {sub && <div className="mb-hero-sub">{sub}</div>}
      {foot && <div className="mb-hero-foot">{foot}</div>}
    </motion.div>
  );
}

/** Horizontally snap-scrolling KPI tiles. Four stats stacked in a 2x2 grid on
 *  a phone shrink every number to unreadable; a swipeable rail keeps them at
 *  full size and tells the user there is more by letting the next tile peek. */
export function StatStrip({ items = [] }) {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className="mb-strip" role="list">
      {shown.map((s, i) => (
        <motion.div
          key={s.label}
          role="listitem"
          className="mb-stat"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 6) * 0.04 }}
        >
          <div className="mb-stat-label">{s.label}</div>
          <div className="mb-stat-value" style={s.color ? { color: s.color } : undefined}>{s.value}</div>
          {s.sub && <div className="mb-stat-sub">{s.sub}</div>}
        </motion.div>
      ))}
    </div>
  );
}

/** Stats that must all be visible at once (never more than four). */
export function StatGrid({ items = [], cols = 2 }) {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className="mb-statgrid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {shown.map(s => (
        <div key={s.label} className="mb-stat">
          <div className="mb-stat-label">{s.label}</div>
          <div className="mb-stat-value" style={s.color ? { color: s.color } : undefined}>{s.value}</div>
          {s.sub && <div className="mb-stat-sub">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function Section({ title, note, action, children }) {
  return (
    <section className="mb-section">
      {(title || action) && (
        <header className="mb-section-head">
          <h2>{title}</h2>
          {action}
        </header>
      )}
      {note && <p className="mb-section-note">{note}</p>}
      {children}
    </section>
  );
}

export function Empty({ children, action }) {
  return (
    <div className="mb-empty">
      <p>{children}</p>
      {action}
    </div>
  );
}

/* ─── Controls ────────────────────────────────────────────────
   Filters and sorts move off the page and into a bottom sheet: a phone
   toolbar with a search box, five chips and a select either wraps into four
   lines or scrolls sideways past the point of discovery. */

export function ControlRail({ query, onQuery, placeholder = 'Search…', activeCount = 0, onOpen, right }) {
  return (
    <div className="mb-rail">
      <div className="mb-search">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4">
          <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
        </svg>
        <input
          value={query}
          onChange={e => onQuery(e.target.value)}
          placeholder={placeholder}
          type="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />
        {query && (
          <button className="mb-search-clear" onClick={() => onQuery('')} aria-label="Clear search">×</button>
        )}
      </div>
      {onOpen && (
        <button className={`mb-filter-btn ${activeCount > 0 ? 'on' : ''}`} onClick={onOpen} aria-label="Filters and sorting">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {activeCount > 0 && <span className="mb-filter-dot">{activeCount}</span>}
        </button>
      )}
      {right}
    </div>
  );
}

/** Two to four mutually exclusive options, always visible. More than that
 *  belongs in the filter sheet. */
export function Segmented({ options, value, onChange }) {
  return (
    <div className="mb-seg" role="tablist">
      {options.map(o => (
        <button
          key={o.key}
          role="tab"
          aria-selected={value === o.key}
          className={value === o.key ? 'on' : ''}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ChipRow({ options, value, onChange, allowToggleOff = false }) {
  return (
    <div className="mb-chips">
      {options.map(o => (
        <button
          key={o.key}
          className={`mb-chip ${value === o.key ? 'on' : ''}`}
          style={value === o.key && o.color
            ? { background: hexToRgba(o.color, 0.18), borderColor: hexToRgba(o.color, 0.5), color: o.color }
            : undefined}
          onClick={() => onChange(allowToggleOff && value === o.key ? 'all' : o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A list of radio-style rows, the shape filter sheets are built from. */
export function OptionList({ label, options, value, onChange }) {
  return (
    <div className="mb-optgroup">
      <div className="mb-optgroup-label">{label}</div>
      {options.map(o => (
        <button
          key={o.key}
          className={`mb-opt ${value === o.key ? 'on' : ''}`}
          onClick={() => onChange(o.key)}
        >
          <span className="mb-opt-label">
            {o.color && <i className="mb-opt-swatch" style={{ background: o.color }} />}
            {o.label}
          </span>
          {value === o.key && (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── Bottom sheet ────────────────────────────────────────────
   Modal surfaces slide from the bottom so they open under the thumb, and
   the page behind is scroll-locked while one is up. */

export function Sheet({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="mb-sheet-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <motion.div
            className="mb-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 38 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-sheet-grip" aria-hidden="true" />
            {title && (
              <div className="mb-sheet-head">
                <h3>{title}</h3>
                <button className="mb-sheet-x" onClick={onClose} aria-label="Close">×</button>
              </div>
            )}
            <div className="mb-sheet-body">{children}</div>
            {footer && <div className="mb-sheet-foot">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Full-width action rows, used inside sheets. */
export function SheetAction({ icon, label, hint, onClick, tone }) {
  return (
    <button className={`mb-sheet-action ${tone ? `tone-${tone}` : ''}`} onClick={onClick}>
      {icon && <span className="mb-sheet-action-icon">{icon}</span>}
      <span className="mb-sheet-action-text">
        <span className="l">{label}</span>
        {hint && <span className="h">{hint}</span>}
      </span>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

/* ─── Bits ────────────────────────────────────────────────── */

export function Fab({ onClick, label = 'Add', children }) {
  return (
    <button className="mb-fab" onClick={onClick} aria-label={label}>
      {children || (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      )}
    </button>
  );
}

export function Thumb({ src, alt = '', size = 'md' }) {
  return (
    <div className={`mb-thumb sz-${size}`}>
      {src ? <img src={src} alt={alt} loading="lazy" /> : <span className="mb-thumb-blank" aria-hidden="true" />}
    </div>
  );
}

export function Tag({ children, color, tone }) {
  const style = color
    ? { background: hexToRgba(color, 0.16), color, borderColor: hexToRgba(color, 0.42) }
    : undefined;
  return <span className={`mb-tag ${tone ? `tone-${tone}` : ''}`} style={style}>{children}</span>;
}

export function Delta({ value, pctValue, profitColor, lossColor }) {
  if (value == null) return <span className="mb-delta flat">no live price</span>;
  const up = value >= 0;
  return (
    <span className={`mb-delta ${up ? 'up' : 'down'}`} style={{ color: up ? profitColor : lossColor }}>
      {up ? '▲' : '▼'} {signed(value)}
      {pctValue != null && <i>{pct(pctValue)}</i>}
    </span>
  );
}

/** Proportional allocation bar — the phone-friendly stand-in for the desktop
 *  pie charts, which lose their legend and their labels below ~400px. */
export function AllocationBar({ parts = [] }) {
  const total = parts.reduce((s, p) => s + (p.value || 0), 0);
  if (total <= 0) return null;
  return (
    <div className="mb-alloc">
      <div className="mb-alloc-bar">
        {parts.map((p, i) => (
          <motion.span
            key={p.name}
            style={{ background: p.color }}
            title={`${p.name} · ${money(p.value)}`}
            initial={{ width: 0 }}
            animate={{ width: `${(p.value / total) * 100}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 8) * 0.05 }}
          />
        ))}
      </div>
      <ul className="mb-alloc-legend">
        {parts.map(p => (
          <li key={p.name}>
            <i style={{ background: p.color }} />
            <span className="n">{p.name}</span>
            <span className="c">{p.count != null ? `${p.count} item${p.count !== 1 ? 's' : ''}` : ''}</span>
            <span className="v">{money(p.value)}</span>
            <span className="p">{((p.value / total) * 100).toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Meter with a filled portion, used for progress-to-target and gain bars. */
export function Meter({ ratio = 0, color, delay = 0 }) {
  return (
    <div className="mb-meter">
      <motion.span
        style={{ background: color }}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
      />
    </div>
  );
}

/** Tiny inline sparkline for price trails. */
export function Spark({ values = [], width = 96, height = 26, invert = false }) {
  const pts = values.filter(v => v != null);
  if (pts.length < 2) return <span className="mb-spark-empty">collecting…</span>;
  const min = Math.min(...pts), max = Math.max(...pts), span = max - min || 1;
  const d = pts
    .map((p, i) => `${((i / (pts.length - 1)) * width).toFixed(1)},${(height - 2 - ((p - min) / span) * (height - 4)).toFixed(1)}`)
    .join(' ');
  const falling = pts[pts.length - 1] <= pts[0];
  // On a wishlist a falling price is good news, so the colour flips.
  const good = invert ? falling : !falling;
  return (
    <svg className="mb-spark" width={width} height={height} aria-hidden="true">
      <polyline points={d} fill="none" stroke={good ? '#4ade80' : '#f87171'} strokeWidth="1.6"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Renders `count` items and reveals the rest on demand — long lists on a
 *  phone are otherwise a scroll trap. */
export function useReveal(list, step = 12) {
  const [limit, setLimit] = React.useState(step);
  const prevLen = useRef(list.length);
  useEffect(() => {
    // A new filter shrinks the list; start over so "Show more" is accurate.
    if (list.length !== prevLen.current) {
      prevLen.current = list.length;
      setLimit(step);
    }
  }, [list.length, step]);
  return [list.slice(0, limit), list.length > limit, () => setLimit(l => l + step)];
}

export function MoreButton({ onClick, remaining }) {
  return (
    <button className="mb-more" onClick={onClick}>
      Show {Math.min(remaining, 12)} more <span>({remaining} left)</span>
    </button>
  );
}
